/**
 * Indicator Dependency Validator
 *
 * Pure functions for validating and managing indicator dependencies.
 * Supports series-as-source feature with circular reference detection
 * and dependency-ordered calculation.
 *
 * All functions are PURE - no side effects, easily testable in isolation.
 *
 * @module indicatorDependencyValidator
 */

import {
  getFeatureLimits,
  getValidationRules,
} from "../config/featureFlags.js";

/**
 * Extract source indicator ID from a sourcePath
 * @param {string} sourcePath - Source path (e.g., "close", "uuid-123.rsi", "uuid-456.signal")
 * @returns {string|null} Indicator ID (UUID) or null if OHLC field
 *
 * @example
 * extractSourceIndicatorId("close") // null (OHLC field)
 * extractSourceIndicatorId("ind_rsi_uuid.rsi") // "ind_rsi_uuid"
 * extractSourceIndicatorId("ind_macd_uuid.signal") // "ind_macd_uuid"
 *
 * IMPORTANT: Returns the IMMUTABLE indicator ID (UUID), not the mutable renderKey.
 * This ensures dependencies persist when indicator config changes (e.g., RSI(14) → RSI(16)).
 */
export function extractSourceIndicatorId(sourcePath) {
  if (!sourcePath || typeof sourcePath !== "string") return null;

  // OHLC and calculated fields don't have dependencies
  const ohlcFields = [
    "open",
    "high",
    "low",
    "close",
    "hl2",
    "hlc3",
    "ohlc4",
    "hlcc4",
  ];
  if (ohlcFields.includes(sourcePath.toLowerCase())) {
    return null;
  }

  // Series reference uses dot notation: "indicatorId.subKey"
  // indicatorId is the IMMUTABLE UUID, not the renderKey
  if (sourcePath.includes(".")) {
    const [indicatorId] = sourcePath.split(".");
    return indicatorId;
  }

  return null;
}

/**
 * Build dependency graph from indicator list
 * @param {Array} indicators - List of indicator configurations
 * @returns {Object} Dependency graph { indicatorId: [dependencyId1, dependencyId2, ...] }
 *
 * @example
 * buildDependencyGraph([
 *   { id: "uuid-1", renderKey: "rsi_14", config: { sourcePath: "close" } },
 *   { id: "uuid-2", renderKey: "ema_9", config: { sourcePath: "uuid-1.rsi" } }
 * ])
 * // Returns: { "uuid-1": [], "uuid-2": ["uuid-1"] }
 *
 * IMPORTANT: Uses IMMUTABLE indicator.id as graph keys, not mutable renderKey.
 * This ensures the graph remains valid when indicator configs change.
 */
export function buildDependencyGraph(indicators) {
  if (!indicators || !Array.isArray(indicators)) {
    return {};
  }

  const graph = {};

  indicators.forEach((indicator) => {
    // ALWAYS use immutable ID, never renderKey
    const id = indicator.id;
    if (!id) return; // Skip invalid indicators

    const sourcePath = indicator.config?.sourcePath;
    const sourceId = extractSourceIndicatorId(sourcePath);

    // Initialize dependency list
    if (sourceId) {
      graph[id] = [sourceId];
    } else {
      graph[id] = []; // No dependencies (uses OHLC)
    }
  });

  return graph;
}

/**
 * Detect circular dependency using Depth-First Search (DFS)
 * @param {Object} graph - Dependency graph from buildDependencyGraph
 * @param {string} startNode - Node to check for cycles
 * @param {Set} visited - Nodes visited in current path (for recursion)
 * @param {Set} recursionStack - Nodes in current DFS path
 * @param {number} iterationCount - Safety counter to prevent infinite loops
 * @returns {Object} { hasCycle: boolean, cyclePath: Array }
 *
 * @example
 * const graph = { a: ["b"], b: ["c"], c: ["a"] };
 * detectCircularDependency(graph, "a")
 * // Returns: { hasCycle: true, cyclePath: ["a", "b", "c", "a"] }
 */
export function detectCircularDependency(
  graph,
  startNode,
  visited = new Set(),
  recursionStack = new Set(),
  iterationCount = 0,
) {
  const limits = getFeatureLimits("SERIES_AS_SOURCE");
  const maxIterations = limits.maxCircularChecks || 100;

  // Safety: Prevent infinite loops
  if (iterationCount > maxIterations) {
    return {
      hasCycle: false,
      cyclePath: [],
      error: "MAX_ITERATIONS_EXCEEDED",
    };
  }

  // Node not in graph = no dependencies = no cycle
  if (!graph[startNode]) {
    return { hasCycle: false, cyclePath: [] };
  }

  // Mark current node as visited and add to recursion stack
  visited.add(startNode);
  recursionStack.add(startNode);

  // Check all dependencies
  const dependencies = graph[startNode] || [];
  for (const dependency of dependencies) {
    // If dependency not visited, recurse
    if (!visited.has(dependency)) {
      const result = detectCircularDependency(
        graph,
        dependency,
        visited,
        recursionStack,
        iterationCount + 1,
      );
      if (result.hasCycle) {
        return result;
      }
    }
    // If dependency is in recursion stack, we found a cycle
    else if (recursionStack.has(dependency)) {
      return {
        hasCycle: true,
        cyclePath: [...recursionStack, dependency],
      };
    }
  }

  // Remove from recursion stack before backtracking
  recursionStack.delete(startNode);

  return { hasCycle: false, cyclePath: [] };
}

/**
 * Check if adding/updating an indicator would create a circular dependency
 * @param {Array} existingIndicators - Current indicators on chart
 * @param {Object} newIndicator - Indicator being added/updated
 * @returns {Object} { wouldCycle: boolean, cyclePath: Array, error: string|null }
 *
 * @example
 * wouldCreateCycle(
 *   [{ id: "uuid-1", renderKey: "rsi_14", config: { sourcePath: "close" } }],
 *   { id: "uuid-2", renderKey: "ema_9", config: { sourcePath: "uuid-1.rsi" } }
 * )
 * // Returns: { wouldCycle: false, cyclePath: [], error: null }
 */
export function wouldCreateCycle(existingIndicators, newIndicator) {
  if (!newIndicator || !newIndicator.id) {
    return {
      wouldCycle: false,
      cyclePath: [],
      error: "Invalid indicator configuration",
    };
  }

  // Build graph with the new/updated indicator
  const testIndicators = [
    ...existingIndicators.filter((i) => i.id !== newIndicator.id),
    newIndicator,
  ];

  const graph = buildDependencyGraph(testIndicators);

  // Check for cycles starting from the new indicator (use ID, not renderKey)
  const result = detectCircularDependency(graph, newIndicator.id);

  return {
    wouldCycle: result.hasCycle,
    cyclePath: result.cyclePath || [],
    error: result.error || null,
  };
}

/**
 * Topological sort using Kahn's algorithm (BFS-based)
 * Orders indicators so dependencies are calculated before dependents
 * @param {Array} indicators - List of indicator configurations
 * @param {Object} graph - Optional pre-built dependency graph
 * @returns {Array} Sorted indicators in calculation order
 *
 * @example
 * topologicalSort([
 *   { id: "uuid-2", renderKey: "ema_9", config: { sourcePath: "uuid-1.rsi" } },
 *   { id: "uuid-1", renderKey: "rsi_14", config: { sourcePath: "close" } }
 * ])
 * // Returns: [rsi_14, ema_9] - RSI calculated first, then EMA
 */
export function topologicalSort(indicators, graph = null) {
  if (!indicators || indicators.length === 0) {
    return [];
  }

  // Build graph if not provided
  const depGraph = graph || buildDependencyGraph(indicators);

  // Build reverse graph (dependents) and calculate in-degrees
  const reverseGraph = {}; // { dependency: [dependent1, dependent2, ...] }
  const inDegree = {}; // { indicator: numberOfDependencies }

  // Initialize all nodes (using immutable ID)
  indicators.forEach((indicator) => {
    const id = indicator.id;
    if (id) {
      inDegree[id] = 0;
      reverseGraph[id] = [];
    }
  });

  // Build reverse graph and in-degrees
  Object.keys(depGraph).forEach((node) => {
    const dependencies = depGraph[node] || [];
    inDegree[node] = dependencies.length;

    dependencies.forEach((dep) => {
      if (!reverseGraph[dep]) {
        reverseGraph[dep] = [];
      }
      reverseGraph[dep].push(node);
    });
  });

  // Queue for nodes with no dependencies
  const queue = [];
  Object.keys(inDegree).forEach((node) => {
    if (inDegree[node] === 0) {
      queue.push(node);
    }
  });

  // Process queue
  const sorted = [];
  while (queue.length > 0) {
    const current = queue.shift();
    sorted.push(current);

    // Reduce in-degree for dependents
    const dependents = reverseGraph[current] || [];
    dependents.forEach((dependent) => {
      inDegree[dependent]--;
      if (inDegree[dependent] === 0) {
        queue.push(dependent);
      }
    });
  }

  // Map sorted IDs back to indicator objects (using immutable ID)
  const idToIndicator = {};
  indicators.forEach((indicator) => {
    const id = indicator.id;
    if (id) {
      idToIndicator[id] = indicator;
    }
  });

  return sorted
    .map((id) => idToIndicator[id])
    .filter((indicator) => indicator !== undefined);
}

/**
 * Calculate maximum dependency depth for an indicator
 * @param {Object} graph - Dependency graph
 * @param {string} nodeId - Indicator ID to check
 * @param {Set} visited - Visited nodes (for memoization)
 * @returns {number} Maximum depth (0 = no dependencies, 1 = direct OHLC source, etc.)
 *
 * @example
 * const graph = { rsi: [], ema: ["rsi"], sma: ["ema"] };
 * getMaxDepth(graph, "sma") // Returns 2 (sma → ema → rsi)
 */
export function getMaxDepth(graph, nodeId, visited = new Set()) {
  if (visited.has(nodeId)) {
    return 0; // Avoid infinite recursion
  }

  const dependencies = graph[nodeId] || [];
  if (dependencies.length === 0) {
    return 0; // No dependencies
  }

  visited.add(nodeId);

  const depths = dependencies.map((dep) =>
    getMaxDepth(graph, dep, new Set(visited)),
  );
  return 1 + Math.max(...depths, 0);
}

/**
 * Get maximum dependency depth across entire graph
 * @param {Object} graph - Dependency graph
 * @returns {number} Maximum depth in the graph
 */
export function getMaxDependencyDepth(graph) {
  if (!graph || Object.keys(graph).length === 0) {
    return 0;
  }

  const depths = Object.keys(graph).map((node) => getMaxDepth(graph, node));
  return Math.max(...depths, 0);
}

/**
 * Find all indicators that depend on a given indicator
 * @param {Array} indicators - List of indicator configurations
 * @param {string} targetId - Indicator ID (UUID) to check dependents for
 * @returns {Array} List of dependent indicators
 *
 * @example
 * findDependents(indicators, "uuid-1")
 * // Returns: [{ id: "uuid-2", renderKey: "ema_9", ... }] - EMA depends on RSI
 */
export function findDependents(indicators, targetId) {
  if (!indicators || !targetId) {
    return [];
  }

  return indicators.filter((indicator) => {
    const sourcePath = indicator.config?.sourcePath;
    const sourceId = extractSourceIndicatorId(sourcePath);
    return sourceId === targetId;
  });
}

/**
 * Check if an indicator can be safely removed
 * @param {Array} indicators - Current indicators on chart
 * @param {string} targetId - Indicator ID (UUID) to remove
 * @returns {Object} { canRemove: boolean, dependents: Array, error: string|null }
 *
 * @example
 * canSafelyRemove(indicators, "uuid-1")
 * // Returns: { canRemove: false, dependents: ["uuid-2"], error: "Has dependents" }
 */
export function canSafelyRemove(indicators, targetId) {
  const rules = getValidationRules("SERIES_AS_SOURCE");

  const dependents = findDependents(indicators, targetId);

  if (dependents.length > 0 && rules.blockRemovalWithDependents) {
    return {
      canRemove: false,
      dependents: dependents.map((d) => d.id), // Return IDs, not renderKeys
      error: "Cannot remove: other indicators depend on this",
    };
  }

  return {
    canRemove: true,
    dependents: [],
    error: null,
  };
}

/**
 * Validate indicator dependencies comprehensively
 * @param {Array} existingIndicators - Current indicators on chart
 * @param {Object} newIndicator - Indicator being added/updated
 * @returns {Object} { valid: boolean, errors: Array, warnings: Array }
 *
 * @example
 * validateIndicatorDependencies(indicators, newIndicator)
 * // Returns: { valid: true, errors: [], warnings: ["Dependency depth is 2"] }
 */
export function validateIndicatorDependencies(
  existingIndicators,
  newIndicator,
) {
  const errors = [];
  const warnings = [];
  const rules = getValidationRules("SERIES_AS_SOURCE");
  const limits = getFeatureLimits("SERIES_AS_SOURCE");

  // Check 1: Circular references
  if (rules.blockCircularReferences) {
    const cycleCheck = wouldCreateCycle(existingIndicators, newIndicator);
    if (cycleCheck.wouldCycle) {
      errors.push(
        `Circular dependency detected: ${cycleCheck.cyclePath.join(" → ")}`,
      );
    }
  }

  // Check 2: Dependency depth
  const testIndicators = [
    ...existingIndicators.filter((i) => i.id !== newIndicator.id),
    newIndicator,
  ];
  const graph = buildDependencyGraph(testIndicators);
  const depth = getMaxDepth(graph, newIndicator.id);

  if (depth >= limits.maxDependencyDepth) {
    errors.push(
      `Dependency chain too deep (${depth}). Maximum allowed: ${limits.maxDependencyDepth}`,
    );
  } else if (
    depth >= limits.warnDependencyDepth &&
    rules.showDependencyWarnings
  ) {
    warnings.push(
      `Dependency chain depth is ${depth}. Consider simplifying for better performance.`,
    );
  }

  // Check 3: Total indicator count
  if (testIndicators.length > limits.maxTotalIndicators) {
    errors.push(
      `Too many indicators (${testIndicators.length}). Maximum allowed: ${limits.maxTotalIndicators}`,
    );
  }

  // Check 4: Source indicator exists
  const sourcePath = newIndicator.config?.sourcePath;
  const sourceId = extractSourceIndicatorId(sourcePath);
  if (sourceId) {
    const sourceExists = existingIndicators.some((i) => i.id === sourceId);
    if (!sourceExists) {
      errors.push(`Source indicator "${sourceId}" not found on chart`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Get dependency chain for an indicator (for display purposes)
 * @param {Object} graph - Dependency graph
 * @param {string} nodeId - Indicator ID
 * @returns {Array} Chain of dependencies from OHLC to this indicator
 *
 * @example
 * getDependencyChain(graph, "sma_20")
 * // Returns: ["close", "rsi_14", "ema_9", "sma_20"]
 */
export function getDependencyChain(graph, nodeId) {
  const chain = [nodeId];
  let current = nodeId;

  while (graph[current] && graph[current].length > 0) {
    const dependency = graph[current][0]; // Take first dependency
    if (chain.includes(dependency)) {
      // Circular reference detected, stop
      chain.push(dependency);
      break;
    }
    chain.unshift(dependency);
    current = dependency;
  }

  return chain;
}
